import { useCallback } from 'react';
import { useMutation, useMutationState } from '@tanstack/react-query';
import { sessionStorageController } from './storages';
import Client from './client';
import type { UseMutationOptions, Mutation } from '@tanstack/react-query';

const PARKING_TICKET_CREATE = ['PARKING_TICKET', 'CREATE'];

export const useParkingTicketCreate = (
  options: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof Client.createParkingTicket>>,
      Error,
      Parameters<typeof Client.createParkingTicket>[0],
      unknown
    >,
    'mutationFn'
  > = {},
) => {
  const { mutateAsync, status } = useMutation({
    mutationFn: Client.createParkingTicket,
    mutationKey: PARKING_TICKET_CREATE,
    ...options,
  });

  const createParkingTicket = useCallback(
    async (variables: Parameters<typeof Client.createParkingTicket>[0]) => {
      const data = await mutateAsync(variables);

      const { data: parkingTicketData } = data;

      sessionStorageController.saveTicketData(parkingTicketData);

      return data;
    },
    [mutateAsync],
  );

  return {
    createParkingTicket,
    createParkingTicketStatus: status,
  };
};

export const useParkingTicketCreateState = () => {
  const data = useMutationState({
    filters: { mutationKey: PARKING_TICKET_CREATE },
    select: (
      mutation: Mutation<
        Awaited<ReturnType<typeof Client.createParkingTicket>>,
        Error,
        Parameters<typeof Client.createParkingTicket>[0],
        unknown
      >,
    ) => mutation.state.data,
  });

  const lastIndex = data ? data?.length - 1 : 0;

  return data?.[lastIndex]?.data ?? sessionStorageController.getTicketData();
};
